import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UsersRound } from 'lucide-react';
import PageHead from '../../components/PageHead/PageHead';
import SubTabs from '../../components/SubTabs/SubTabs';
import Team from '../Team/Team';
import Teams from '../Teams/Teams';
import { useI18n } from '../../i18n/I18nContext';
import './TeamHub.css';

/**
 * Unified staff hub — merges the old "Team" (every account) and "Teams" (org groups) into one
 * place with a segmented control. Deep-linkable via ?tab=people|teams; the legacy /teams route
 * redirects to ?tab=teams.
 *
 * One page header (the route's only h1), then the SubTabs strip with the tab's one primary action
 * beside it. The People tab draws its button through `actionsSlot` (a portal into `.th-actions`);
 * the Teams tab's own button is lifted into the same cell by TeamHub.css.
 */
const TABS = [
  { key: 'people', labelKey: 'teamCalendar.hub.tabPeople' },
  { key: 'teams', labelKey: 'team.tabTeams' },
];

function TeamHub() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const active = params.get('tab') === 'teams' ? 'teams' : 'people';
  // A callback ref in state: the tab re-renders once the slot exists and draws its button there.
  const [actionsSlot, setActionsSlot] = useState(null);

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    next.set('tab', key);
    setParams(next, { replace: true });
  };

  const tabs = TABS.map((tab) => ({ ...tab, label: t(tab.labelKey) }));

  return (
    <div className="th-page">
      <PageHead icon={<UsersRound />} title={t('team.title')} description={t('teamCalendar.hub.subtitle')} />

      {/* Not a role="tabpanel": this box also holds the tab strip itself (a panel must hold only what the tab shows) */}
      <div className={`th-panel th-panel--${active}`}>
        <SubTabs className="th-tabs" tabs={tabs} active={active} onChange={setTab} ariaLabel={t('team.title')} />
        <div className="th-actions" ref={setActionsSlot} />
        {active === 'people' ? <Team embedded actionsSlot={actionsSlot} /> : <Teams embedded />}
      </div>
    </div>
  );
}

export default TeamHub;
